import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Save, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../lib/api';
import { US_STATES } from '../lib/shop';
import { TextField, TextArea, SelectField, Check } from './AdminFields';

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const errMsg = (e) => {
    const d = e.response?.data?.detail;
    if (Array.isArray(d) && d[0]?.loc?.includes('wave_payment_link')) return 'Wave link must start with http:// or https://';
    return typeof d === 'string' ? d : 'Please check the fields and try again.';
};

const PAY_COLORS = {
    paid: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40',
    processing: 'bg-amber-500/15 text-amber-700 border-amber-500/40',
    awaiting_payment: 'bg-sky-500/15 text-sky-700 border-sky-500/40',
    pending: 'bg-white/5 text-white/50 border-white/20',
};

// ---- Shop settings ----------------------------------------------------------
const ShopSettings = () => {
    const [s, setS] = useState(null);
    const [msg, setMsg] = useState('');
    useEffect(() => { api.get('/admin/shop-settings').then((r) => setS(r.data)); }, []);
    if (!s) return null;
    const save = async () => {
        try {
            await api.put('/admin/shop-settings', {
                ...s, flat_shipping: parseFloat(s.flat_shipping) || 0,
                free_shipping_threshold: parseFloat(s.free_shipping_threshold) || 0, nc_tax_rate: parseFloat(s.nc_tax_rate) || 0,
            });
            setMsg('Saved');
        } catch (e) { setMsg(errMsg(e)); }
    };
    return (
        <div className="border border-white/10 bg-brand-surface p-5 mb-8" data-testid="admin-shop-settings">
            <div className="label-caps text-brand-primary mb-4">Shipping, tax & order alerts</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <TextField label="Flat shipping ($)" type="number" value={s.flat_shipping} onChange={(v) => setS({ ...s, flat_shipping: v })} testid="ss-flat-shipping" />
                <TextField label="Free shipping over ($, 0 = never)" type="number" value={s.free_shipping_threshold} onChange={(v) => setS({ ...s, free_shipping_threshold: v })} testid="ss-free-threshold" />
                <TextField label="NC sales tax (%)" type="number" value={s.nc_tax_rate} onChange={(v) => setS({ ...s, nc_tax_rate: v })} testid="ss-nc-tax" />
                <TextField label="Order alert email" value={s.order_alert_email} onChange={(v) => setS({ ...s, order_alert_email: v })} testid="ss-alert-email" />
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Wave payment link (card) — shown to invoice buyers" value={s.wave_payment_link || ''} onChange={(v) => setS({ ...s, wave_payment_link: v })} testid="ss-wave-link" placeholder="https://…" />
                <TextArea label="Bank wire / ACH instructions — shown to wire buyers" rows={4} value={s.wire_instructions || ''} onChange={(v) => setS({ ...s, wire_instructions: v })} testid="ss-wire-instructions" />
            </div>
            <div className="mt-4 flex items-center gap-4">
                <button onClick={save} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors" data-testid="ss-save"><Save className="h-4 w-4" /> Save settings</button>
                {msg && <span className="text-sm text-white/60" data-testid="ss-msg">{msg}</span>}
            </div>
            <p className="mt-3 text-xs text-white/40">Tax is only added for orders shipping to North Carolina (applied to items + shipping).</p>
        </div>
    );
};

// ---- Orders -----------------------------------------------------------------
const OrderRow = ({ o, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ fulfillment_status: o.fulfillment_status, tracking_carrier: o.tracking_carrier || '', tracking_number: o.tracking_number || '', admin_notes: o.admin_notes || '' });
    const [saving, setSaving] = useState(false);
    const c = o.customer;
    const save = async () => {
        setSaving(true);
        try { await api.put(`/admin/orders/${o.id}`, f); onSaved(); } catch (e) { window.alert(errMsg(e)); } finally { setSaving(false); }
    };
    const markPaid = async () => {
        if (!window.confirm(`Mark ${o.order_number} as paid? The buyer gets a payment receipt.`)) return;
        setSaving(true);
        try { await api.post(`/admin/orders/${o.id}/mark-paid`); onSaved(); } catch (e) { window.alert(errMsg(e)); } finally { setSaving(false); }
    };
    return (
        <div className="border-b border-white/10" data-testid={`admin-order-${o.id}`}>
            <button onClick={() => setOpen(!open)} className="w-full p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-center text-left hover:bg-white/5 transition-colors" data-testid={`admin-order-toggle-${o.id}`}>
                <span className="font-display font-bold text-white">{o.order_number}</span>
                <span className="text-sm text-white/70 truncate">{c.name}</span>
                <span className="text-sm text-white/50">{new Date(o.created_at).toLocaleDateString()}</span>
                <span className="font-bold text-white">{money(o.total)}</span>
                <span className={`label-caps text-[10px] border px-2 py-1 w-fit ${PAY_COLORS[o.payment_status] || 'bg-red-500/15 text-red-700 border-red-500/40'}`} data-testid={`admin-order-pay-${o.id}`}>
                    {o.payment_status} · {o.payment_method}
                </span>
                <span className="label-caps text-xs text-white/60 flex items-center justify-between">{o.fulfillment_status}{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
            </button>
            {open && (
                <div className="p-5 bg-brand-bg grid grid-cols-1 md:grid-cols-3 gap-6" data-testid={`admin-order-details-${o.id}`}>
                    <div className="text-sm text-white/70 space-y-1">
                        <div className="label-caps text-white/40 mb-2">Customer</div>
                        <div>{c.name}{c.company ? ` · ${c.company}` : ''}</div>
                        <div><a href={`mailto:${c.email}`} className="text-brand-primary">{c.email}</a> · {c.phone}</div>
                        <div>{c.address1}{c.address2 ? `, ${c.address2}` : ''}</div>
                        <div>{c.city}, {c.state} {c.zip}</div>
                        {c.notes && <div className="pt-2 text-white/50">Notes: {c.notes}</div>}
                    </div>
                    <div className="text-sm text-white/70 space-y-1">
                        <div className="label-caps text-white/40 mb-2">Items</div>
                        {o.lines.map((l) => <div key={l.machine_id} className="flex justify-between gap-2"><span>{l.qty} × {l.name}</span><span>{money(l.line_total)}</span></div>)}
                        <div className="flex justify-between pt-2 border-t border-white/10"><span>Shipping</span><span>{money(o.shipping)}</span></div>
                        {o.tax > 0 && <div className="flex justify-between"><span>{o.tax_label}</span><span>{money(o.tax)}</span></div>}
                        <div className="flex justify-between font-bold text-white"><span>Total</span><span>{money(o.total)}</span></div>
                    </div>
                    <div className="space-y-3">
                        {o.payment_status === 'awaiting_payment' && (
                            <button onClick={markPaid} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-on-dark font-bold px-4 py-2.5 label-caps transition-colors" data-testid={`admin-order-mark-paid-${o.id}`}>
                                Mark as paid
                            </button>
                        )}
                        <SelectField label="Fulfillment" value={f.fulfillment_status} onChange={(v) => setF({ ...f, fulfillment_status: v })} testid={`admin-order-status-${o.id}`}
                            options={[['new', 'New'], ['shipped', 'Shipped'], ['delivered', 'Delivered'], ['cancelled', 'Cancelled']]} />
                        <SelectField label="Carrier" value={f.tracking_carrier} onChange={(v) => setF({ ...f, tracking_carrier: v })} testid={`admin-order-carrier-${o.id}`}
                            options={[['', '—'], ['UPS', 'UPS'], ['FedEx', 'FedEx'], ['USPS', 'USPS'], ['Freight', 'Freight'], ['Other', 'Other']]} />
                        <TextField label="Tracking number" value={f.tracking_number} onChange={(v) => setF({ ...f, tracking_number: v })} testid={`admin-order-tracking-${o.id}`} />
                        <TextArea label="Internal notes" rows={2} value={f.admin_notes} onChange={(v) => setF({ ...f, admin_notes: v })} testid={`admin-order-notes-${o.id}`} />
                        <p className="text-xs text-white/50" data-testid={`admin-order-shipped-note-${o.id}`}>
                            {o.shipped_email_sent_at ? `Shipped email sent ${new Date(o.shipped_email_sent_at).toLocaleString()}` : 'Saving as Shipped emails the buyer their tracking number (once).'}
                        </p>
                        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors" data-testid={`admin-order-save-${o.id}`}>
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export const OrdersAdmin = () => {
    const [orders, setOrders] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const load = () => api.get('/admin/orders').then((r) => setOrders(r.data));
    useEffect(() => { load(); }, []);
    const shown = showAll ? orders : orders.filter((o) => ['paid', 'processing', 'awaiting_payment'].includes(o.payment_status));
    return (
        <div data-testid="admin-orders">
            <h2 className="font-display font-black uppercase text-3xl mb-6">Orders</h2>
            <ShopSettings />
            <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-white/50" data-testid="admin-orders-count">{shown.length} order{shown.length === 1 ? '' : 's'}</span>
                <Check label="Show unpaid / abandoned checkouts" checked={showAll} onChange={setShowAll} testid="admin-orders-show-all" />
            </div>
            <div className="border border-white/10" data-testid="admin-orders-list">
                {shown.length === 0 && <div className="p-6 text-white/50">No orders yet.</div>}
                {shown.map((o) => <OrderRow key={o.id} o={o} onSaved={load} />)}
            </div>
        </div>
    );
};

// ---- Locations --------------------------------------------------------------
const EMPTY_LOC = { name: '', kind: 'partner', city: '', state: 'NC', address: '', lat: '', lng: '', phone: '', email: '', website: '', description: '', services: '', published: true };

const LocationForm = ({ initial, onCancel, onSaved }) => {
    const [f, setF] = useState(initial);
    const [err, setErr] = useState('');
    const [saving, setSaving] = useState(false);
    const upd = (k, v) => setF({ ...f, [k]: v });
    const save = async () => {
        setSaving(true); setErr('');
        const payload = {
            ...f, lat: f.lat === '' || f.lat == null ? null : parseFloat(f.lat), lng: f.lng === '' || f.lng == null ? null : parseFloat(f.lng),
            services: String(f.services || '').split(',').map((s) => s.trim()).filter(Boolean),
        };
        try {
            if (f.id) await api.put(`/admin/partner-locations/${f.id}`, payload);
            else await api.post('/admin/partner-locations', payload);
            onSaved();
        } catch (e) { setErr(errMsg(e)); setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-location-modal">
            <div className="w-full max-w-2xl bg-brand-surface border border-white/10 max-h-[calc(100vh-2rem)] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                    <h3 className="font-display font-black uppercase text-2xl">{f.id ? 'Edit' : 'New'} location</h3>
                    <button onClick={onCancel} className="text-white/60 hover:text-white" data-testid="admin-location-close"><X /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <TextField label="Name *" value={f.name} onChange={(v) => upd('name', v)} testid="lf-name" />
                        <SelectField label="Pin type" value={f.kind} onChange={(v) => upd('kind', v)} testid="lf-kind" options={[['dib', 'Dry Ice Bulls location'], ['partner', 'Authorized partner / operator']]} />
                        <TextField label="City *" value={f.city} onChange={(v) => upd('city', v)} testid="lf-city" />
                        <SelectField label="State *" value={f.state} onChange={(v) => upd('state', v)} testid="lf-state" options={US_STATES.map((s) => [s, s])} />
                        <TextField label="Street address (optional)" value={f.address} onChange={(v) => upd('address', v)} testid="lf-address" />
                        <div className="grid grid-cols-2 gap-2">
                            <TextField label="Latitude" value={f.lat ?? ''} onChange={(v) => upd('lat', v)} testid="lf-lat" />
                            <TextField label="Longitude" value={f.lng ?? ''} onChange={(v) => upd('lng', v)} testid="lf-lng" />
                        </div>
                        <TextField label="Phone" value={f.phone} onChange={(v) => upd('phone', v)} testid="lf-phone" />
                        <TextField label="Email" value={f.email} onChange={(v) => upd('email', v)} testid="lf-email" />
                        <TextField label="Website" value={f.website} onChange={(v) => upd('website', v)} testid="lf-website" />
                        <TextField label="Services (comma separated)" value={f.services} onChange={(v) => upd('services', v)} testid="lf-services" />
                    </div>
                    <TextArea label="Short description" rows={3} value={f.description} onChange={(v) => upd('description', v)} testid="lf-description" />
                    <Check label="Show on map" checked={f.published} onChange={(v) => upd('published', v)} testid="lf-published" />
                    <p className="text-xs text-white/40">Leave latitude/longitude blank to place the pin automatically from the city and state.</p>
                    {err && <p className="text-sm text-red-600" data-testid="lf-error">{err}</p>}
                </div>
                <div className="flex justify-end gap-3 p-6 border-t border-white/10">
                    <button onClick={onCancel} className="label-caps border border-white/20 hover:bg-white/10 px-5 py-3">Cancel</button>
                    <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-5 py-3 label-caps" data-testid="lf-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export const LocationsAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get('/admin/partner-locations').then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const remove = async (id) => {
        if (!window.confirm('Delete this location?')) return;
        await api.delete(`/admin/partner-locations/${id}`);
        load();
    };
    return (
        <div data-testid="admin-locations">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-black uppercase text-3xl">Map locations</h2>
                <button onClick={() => setEditing({ ...EMPTY_LOC })} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps" data-testid="admin-locations-new">
                    <Plus className="h-4 w-4" /> New location
                </button>
            </div>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-locations-list">
                {items.length === 0 && <div className="p-6 text-white/50">No locations yet — add your first pin.</div>}
                {items.map((l) => (
                    <div key={l.id} className="p-4 flex items-center gap-4">
                        <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: l.kind === 'dib' ? '#F7941D' : '#888' }} />
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate">{l.name}</div>
                            <div className="text-sm text-white/50">{l.city}, {l.state} • {l.kind === 'dib' ? 'Dry Ice Bulls' : 'Partner'} • {l.published ? 'On map' : 'Hidden'}</div>
                        </div>
                        <button onClick={() => setEditing({ ...l, services: (l.services || []).join(', ') })} className="p-2 border border-white/10 hover:bg-white/10" data-testid={`admin-location-edit-${l.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(l.id)} className="p-2 border border-red-500/60 text-red-600 hover:bg-red-500/10" data-testid={`admin-location-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>
            {editing && <LocationForm initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
};
